/**
 * Copy for the recycle bin (FIL-11).
 *
 * Sentence case throughout — `Eyebrow` and `Badge` apply their own casing.
 * Anything interpolating a row's name is a **function**, because a per-row
 * control needs an accessible name a screen reader can tell from its neighbours.
 */
export const RECYCLE_BIN_COPY = {
  eyebrow: 'Recycle bin',
  title: 'Deleted files',
  backToSpaces: 'Back to shared spaces',

  /**
   * The entry point in the Files surface header, beside Connect an agency.
   *
   * **A button, not a card.** It used to be a full-width row with its own
   * count-and-countdown sentence — see git history for that copy — but a
   * header is not where a sentence lives, and `FilesNav`'s standing sidebar
   * entry already gives every principal an always-reachable way in
   * regardless of what this button says. It also used to carry a count badge;
   * that made it visibly larger than "Connect an agency" beside it, so the
   * label alone is what survives — someone who needs the count still has the
   * bin's own table.
   *
   * **It renders nothing on an empty bin.** The sidebar entry is the way in
   * when there is nothing to restore; a second, always-on "Recycle bin"
   * button beside it would just be the same link twice.
   */
  entry: {
    label: 'Recycle bin',
  },

  /**
   * The lead line. It says "your agency's", because that is the boundary: a
   * counterpart's deletion is in *their* bin and never in this one (PERM-4).
   *
   * "Folders," not "shared spaces" — the Files surface lists folder
   * destinations now (`FolderDestinations`, FIL-1d, FIL-1e), and a space is
   * no longer a room this reader navigates into on the way there.
   */
  summary: 'Files your agency deleted, across all folders you can reach.',

  columns: {
    name: 'File',
    size: 'Size',
    origin: 'From',
    deletedBy: 'Deleted by',
    deletedAt: 'Deleted',
    timeLeft: 'Time left',
    actions: 'Actions',
  },

  /**
   * `folder · space` — the same separator the file table uses for provenance.
   *
   * **The space's name, not the counterpart's** (CON-22). A bin row says which
   * folder in which room a file came out of; naming the other agency worked only
   * while a room could have exactly one.
   */
  /**
   * Where a deleted file came from.
   *
   * **Said once when the two are the same string** (FIL-1e). A file deleted
   * from a space's root carries the space's name as its folder name, because
   * that is what the root is called everywhere else — and "Acme · Acme" is a
   * separator with nothing on either side of it. A folder somebody named after
   * its own room reads the same way and gets the same treatment.
   */
  origin: (folderName: string, spaceName: string) =>
    folderName === spaceName ? spaceName : `${folderName} · ${spaceName}`,
  originLink: (folderName: string) => `Open ${folderName}`,

  /**
   * Days remaining (UPL-10).
   *
   * Zero is worded as an event rather than a count, because that is what it is:
   * purge is a job, so a file past its window is waiting for the next sweep.
   */
  daysLeft: (days: number) => (days === 1 ? '1 day' : `${String(days)} days`),
  purgesNext: 'Purges on the next sweep',

  restore: {
    label: 'Restore',
    action: (fileName: string) => `Restore ${fileName}`,
    working: 'Restoring…',
    failed: 'That file could not be restored.',
  },

  /**
   * Emptying the bin (FIL-11b).
   *
   * **The confirmation says the files are destroyed, not that the bin is
   * cleared.** "Empty the recycle bin" is the desktop metaphor and it is
   * exactly wrong here: nothing else in this product destroys evidence on a
   * person's say-so in one act, and the thirty days are the only thing that
   * makes a mistaken delete recoverable. So the prompt names the count, says
   * permanently, and says the word that cannot be walked back.
   *
   * **It counts what the caller may actually purge**, which is not always what
   * the table shows: a member's bin can hold rows an admin deleted, and those
   * are neither theirs to restore nor theirs to destroy (FIL-11a). `remaining`
   * is how the result says so without a second request.
   */
  purge: {
    label: 'Empty recycle bin',
    confirm: {
      title: 'Empty the recycle bin?',
      body: (count: number) =>
        count === 1
          ? 'This permanently destroys 1 file. It cannot be undone, and the file cannot be restored afterwards.'
          : `This permanently destroys ${String(count)} files. It cannot be undone, and they cannot be restored afterwards.`,
      submit: 'Destroy permanently',
      cancel: 'Keep them',
    },
    working: 'Emptying…',
    /**
     * Said after the fact, because the count is the only evidence left that
     * anything happened — the rows it names are gone from the table above.
     */
    done: (purged: number) =>
      purged === 1
        ? '1 file destroyed permanently.'
        : `${String(purged)} files destroyed permanently.`,
    /**
     * The honest half. A member who emptied their own deletions and still sees
     * rows needs to know why, and "ask your administrator" is the whole of the
     * mechanism — the same sentence USR-14's unlock already requires somebody
     * to say out loud.
     */
    remaining: (remaining: number) =>
      remaining === 1
        ? '1 file stays: it was deleted by an administrator at your agency, so only they can remove it.'
        : `${String(remaining)} files stay: they were deleted by an administrator at your agency, so only they can remove them.`,
    failed: 'The recycle bin could not be emptied.',
  },

  units: ['B', 'KB', 'MB', 'GB', 'TB'],

  states: {
    loading: 'Loading the recycle bin…',
    empty: 'Nothing deleted. Files your agency removes appear here for 30 days.',
    tableCaption: 'Files your agency has deleted',
    /**
     * `truncated` — a prefix, not the set. A `status` and never an `alert`:
     * nothing failed, and the rows on screen are all real.
     */
    truncated: 'Showing the most recent deletions. Some older ones are not listed.',
    error: 'The recycle bin could not be loaded.',
    forbidden: 'You do not have access to the recycle bin.',
    retry: 'Try again',
  },

  dateLocale: 'en-US',
} as const;
